import { memo, useContext, useEffect, useState } from 'react';
import { Grid, SelectChangeEvent } from '@mui/material';
import DataTypeSelector from './DataTypeSelector';
import FieldsSettingsIcons from './FieldsSettingsIcons';
import FormatSelector from './FormatSelector';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { FieldsSettingsProps, parseCompositeFormatKey } from './FieldsSettingsTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import errorResponses from '../../../errorResponses';

const FieldsSettings = ({
  clearFieldsFormats,
  dataTypesPadding,
  dataTypesResponse,
  field,
  setClearFieldsFormats,
  trainingSetDataTypeConfig,
}: FieldsSettingsProps): JSX.Element => {
  const [handleFieldChange, setHandleFieldChange]: any = useState({});
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const { mutateAsync: saveField } = useMutation({
    mutationFn: async (params: any) =>
      (await glynt).fields.update(params.id, params.data)
  });
  const updateField = async (dataTypeSelected: string, dataTypeConfigSelected: string | null) => {
    try {
      const fieldDataType = dataTypeSelected.toLowerCase();
      let dataTypeConfig: any = {};

      // TO-DO: refactor the following ifs:   ------- \\
      switch (fieldDataType) {
        case 'date':
          if (dataTypeConfigSelected) {
            dataTypeConfig.order = dataTypeConfigSelected;
          }
          break;

        case 'amount':
        case 'number':
          if (dataTypeConfigSelected) {
            // Parse composite format key (e.g., ".|3, 3" -> { decimalSymbol: '.', thousandsGrouping: '3, 3' })
            const { decimalSymbol, thousandsGrouping } =
              parseCompositeFormatKey(dataTypeConfigSelected);
            dataTypeConfig.decimalSymbol = decimalSymbol;
            dataTypeConfig.thousandsGrouping = thousandsGrouping;
          }
          break;

        case 'string':
          dataTypeConfig = {};
          break;

        default:
          dataTypeConfig = {};
          break;
      }

      await saveField(
        {
          id: field.id,
          data: {
            data_type: fieldDataType,
            ...(Object.keys(dataTypeConfig).length > 0 && { data_type_config: dataTypeConfig }),
          },
        },
        {
          onSuccess: (data) => {
            queryClient.invalidateQueries({
              queryKey: ['fields']
            });
            queryClient.invalidateQueries({
              queryKey: ['trainingSet']
            });
            setHandleFieldChange(data);

            setToast({
              open: true,
              message: 'Field updated successfully.',
              variant: 'success',
            });
          },
          onError: (error) => {
            setToast({
              open: true,
              message: errorResponses.failedToUpdateField,
              variant: 'error',
            });
          }
        }
      );
    } catch (error) {
      setToast({
        open: true,
        message: errorResponses.failedToUpdateField,
        variant: 'error',
      });
    }
  };

  const handleDatatypeSelectorChange = (event: SelectChangeEvent<unknown>) => {
    const dataTypeValue = event.target.value as string;
    updateField(dataTypeValue, null);
  };
  const handleFormatSelectorChange = (event: SelectChangeEvent<unknown>) => {
    const dataTypeConfigValue = event.target.value as string;
    updateField(handleFieldChange.data_type, dataTypeConfigValue);
  };

  useEffect(() => {
    setHandleFieldChange(field);
  }, [field]);

  useEffect(() => {
    if (clearFieldsFormats === true) {
      handleFieldChange.data_type_config = null;
      setClearFieldsFormats(false);
    }
  }, [clearFieldsFormats, setClearFieldsFormats, handleFieldChange]);

  return (
    <>
      <Grid item={true} xs={2} lg={1} style={{ paddingLeft: dataTypesPadding }}>
        {handleFieldChange.data_type && (
          <DataTypeSelector
            dataTypesResponse={dataTypesResponse}
            dropdownChange={handleDatatypeSelectorChange}
            fieldDataType={handleFieldChange.data_type}
          />
        )}
      </Grid>

      <Grid
        item={true}
        xs={2}
        lg={1}
        style={{
          paddingLeft: dataTypesPadding,
          display: 'flex',
        }}
      >
        <div style={{ display: 'inline-block', width: '100%' }}>
          {handleFieldChange.data_type && (
            <FormatSelector
              dataTypesResponse={dataTypesResponse}
              dropdownChange={handleFormatSelectorChange}
              fieldDataType={handleFieldChange.data_type}
              fieldDataTypeConfig={handleFieldChange.data_type_config}
              fieldLabel={handleFieldChange.label}
              trainingSetDataTypeConfig={trainingSetDataTypeConfig}
            />
          )}
        </div>
        {handleFieldChange.data_type !== 'string' ? (
          <div
            data-cy={`global-format-${handleFieldChange.label}`}
            style={{ display: 'inline-block' }}
          >
            <FieldsSettingsIcons
              fieldDataTypeIsGlobal={handleFieldChange.data_type_config?.inherited}
            />
          </div>
        ) : null}
      </Grid>
    </>
  );
};

export default memo(FieldsSettings);
